import { useCallback, useEffect, useState } from 'react'
import { ThemeChangedEvent } from '@/constants/custom-event'
import { defaultTheme } from './constant'
import { ThemeEnum } from './enum'

/**
 * 主题切换 Hook
 */
export const useTheme = () => {
  const [theme, setTheme] = useState<ThemeEnum>(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeEnum
    return savedTheme || defaultTheme
  })

  const getCurrentTheme = useCallback(() => {
    if (theme === ThemeEnum.System) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? ThemeEnum.Dark : ThemeEnum.Light
    }
    return theme
  }, [theme])

  const applyTheme = useCallback((themeValue: ThemeEnum) => {
    const root = window.document.documentElement

    root.classList.remove(ThemeEnum.Light, ThemeEnum.Dark)

    if (themeValue === ThemeEnum.System) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? ThemeEnum.Dark : ThemeEnum.Light
      root.classList.add(systemTheme)
    } else {
      root.classList.add(themeValue)
    }

    localStorage.setItem('theme', themeValue)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => {
      switch (prevTheme) {
        case ThemeEnum.Dark:
          return ThemeEnum.Light
        case ThemeEnum.Light:
          return ThemeEnum.System
        case ThemeEnum.System:
          return ThemeEnum.Dark
        default:
          return ThemeEnum.Dark
      }
    })
  }, [])

  const setThemeMode = useCallback((newTheme: ThemeEnum) => {
    setTheme(newTheme)
  }, [])

  useEffect(() => {
    applyTheme(theme)
  }, [theme, applyTheme])

  useEffect(() => {
    if (theme !== ThemeEnum.System) return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleSystemThemeChange = () => {
      const systemTheme = mediaQuery.matches ? ThemeEnum.Dark : ThemeEnum.Light
      const root = document.documentElement

      // 更新 DOM 类
      root.classList.remove(ThemeEnum.Light, ThemeEnum.Dark)
      root.classList.add(systemTheme)

      // 触发自定义事件，通知 Monaco Editor 更新主题
      window.dispatchEvent(new CustomEvent(ThemeChangedEvent, { detail: { theme: systemTheme } }))
    }

    // 初始设置
    handleSystemThemeChange()

    // 监听系统主题变化
    mediaQuery.addEventListener('change', handleSystemThemeChange)

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [theme])

  // 手动主题变化时也触发事件
  useEffect(() => {
    if (theme !== ThemeEnum.System) {
      window.dispatchEvent(new CustomEvent(ThemeChangedEvent, { detail: { theme } }))
    }
  }, [theme])

  return { theme, toggleTheme, setThemeMode, getCurrentTheme, isDark: getCurrentTheme() === ThemeEnum.Dark }
}
